import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { OffMarketListingResource, UpsertOffMarketListingResource } from "api/resource/off-market-listing";
import { RichTextEditor } from "component/shared/rich-text-editor";
import { Select } from "component/shared/select";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { OffMarketStatus } from "type/off-market-status";
import { OffMarketPropertyType } from "type/off-market-property-type";
import { buildAgentFields } from "shared/agent-fields";
import { AgentControl } from "type/agent-control";

export interface OffMarketFormFlags {
	// MAX-4194: one field, derived server-side. `featureAgentOffice` is deliberately NOT here —
	// the "Show On Agent Page" *column* is read from the list page's own selector, never from
	// this prop, and its only in-form uses were the two gate lines this replaced.
	agentControl: AgentControl;
}

interface AgentOption {
	id: number;
	name: string;
}

interface Props {
	listing: OffMarketListingResource | null;
	flags: OffMarketFormFlags;
	/**
	 * MAX-4194. The agent's "First Last" name resolved from a stored AID when the row has no
	 * typed name, or "" when there is nothing to seed. Resolved in edit.tsx — see the note there
	 * on why agentOptions cannot supply it.
	 */
	agentNameSeed: string;
	agents: AgentOption[];
	saving: boolean;
	/** Resolves true when the save landed — MAX-4194 re-baselines the agent dirty check on success. */
	onSave: (body: UpsertOffMarketListingResource) => Promise<boolean>;
	onCancel: () => void;
}

interface FormState {
	status: string;
	showOnWebsite: boolean;
	showOnAgentPage: boolean;
	streetNumber: string;
	streetName: string;
	unitNumber: string;
	city: string;
	state: string;
	zip: string;
	fullPartialAddress: string;
	propertyType: string;
	bedrooms: number;
	fullBaths: number;
	halfBaths: number;
	squareFeet: string;
	yearBuilt: string;
	listPrice: string;
	soldPrice: string;
	showSoldPrice: boolean;
	soldDate: string;
	agentId: number;
	agentName: string;
	propDescription: string;
	tourYn: boolean;
	tourUrl: string;
	tourLink: string;
	extraLinkYn: boolean;
	extraLinkUrl: string;
	extraLinkText: string;
	mapYn: boolean;
}

const NO_AGENT: AgentOption = { id: 0, name: "No agent" };

interface NumberOption {
	value: number;
	label: string;
}

const BEDROOM_OPTIONS: NumberOption[] = Array.from({ length: 13 }, (_, i) => ({ value: i, label: i === 0 ? "Any" : String(i) }));
const FULL_BATH_OPTIONS: NumberOption[] = Array.from({ length: 9 }, (_, i) => ({ value: i, label: String(i) }));
const HALF_BATH_OPTIONS: NumberOption[] = Array.from({ length: 4 }, (_, i) => ({ value: i, label: String(i) }));

function toNumberOrNull(value: string): number | null {
	if (value === null || value.trim() === "") {
		return null;
	}
	const cleaned = value.replace(/,/g, "");
	const parsed = Number(cleaned);
	return isNaN(parsed) ? null : parsed;
}

// ReactQuill normalizes plain-text MLS remarks to HTML (<p>…</p>, trailing newlines) on mount, so a raw
// string compare against the MLS default gives false positives. Compare on text content instead.
function htmlToText(html: string): string {
	return (html || "")
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function initialState(listing: OffMarketListingResource | null): FormState {
	if (listing) {
		return {
			status: listing.status || OffMarketStatus.UNKNOWN.id,
			showOnWebsite: !!listing.showOnWebsite,
			showOnAgentPage: !!listing.showOnAgentPage,
			streetNumber: listing.streetNumber || "",
			streetName: listing.streetName || "",
			unitNumber: listing.unitNumber || "",
			city: listing.city || "",
			state: listing.state || "",
			zip: listing.zip || "",
			fullPartialAddress: listing.fullPartialAddress || "F",
			propertyType: listing.propertyType || "SFR",
			bedrooms: listing.bedrooms == null ? 1 : listing.bedrooms,
			fullBaths: listing.fullBaths == null ? 1 : listing.fullBaths,
			halfBaths: listing.halfBaths == null ? 0 : listing.halfBaths,
			squareFeet: listing.squareFeet == null ? "" : String(listing.squareFeet),
			yearBuilt: listing.yearBuilt || "",
			listPrice: listing.listPrice == null ? "" : String(listing.listPrice),
			soldPrice: listing.soldPrice == null ? "" : String(listing.soldPrice),
			showSoldPrice: !!listing.showSoldPrice,
			soldDate: listing.soldDate ? String(listing.soldDate).substring(0, 10) : "",
			agentId: listing.agentId || 0,
			agentName: listing.agentName || "",
			// Seed the editor with the saved override if present, else the live MLS remarks so the agent
			// edits from the real text. handleSave only persists an override when it differs from the MLS
			// default (see resolveDescription), so an unedited seed keeps tracking the feed.
			propDescription:
				listing.propDescription && listing.propDescription.trim()
					? listing.propDescription
					: listing.remarks || "",
			tourYn: !!listing.tourYn,
			tourUrl: listing.tourUrl || "",
			tourLink: listing.tourLink || "Virtual Tour",
			extraLinkYn: !!listing.extraLinkYn,
			extraLinkUrl: listing.extraLinkUrl || "",
			extraLinkText: listing.extraLinkText || "Click Here!",
			mapYn: listing.mapYn == null ? true : !!listing.mapYn,
		};
	}
	// Add-mode defaults (soldsmodify_output_1.cfm:24-36)
	return {
		status: OffMarketStatus.UNKNOWN.id,
		showOnWebsite: true,
		showOnAgentPage: true,
		streetNumber: "",
		streetName: "",
		unitNumber: "",
		city: "",
		state: "",
		zip: "",
		fullPartialAddress: "F",
		propertyType: "SFR",
		bedrooms: 1,
		fullBaths: 1,
		halfBaths: 0,
		squareFeet: "0",
		yearBuilt: "",
		listPrice: "",
		soldPrice: "",
		showSoldPrice: false,
		soldDate: "",
		agentId: 0,
		agentName: "",
		propDescription: "",
		tourYn: false,
		tourUrl: "",
		tourLink: "Virtual Tour",
		extraLinkYn: false,
		extraLinkUrl: "",
		extraLinkText: "Click Here!",
		mapYn: true,
	};
}

export const OffMarketForm: React.FC<Props> = ({ listing, flags, agentNameSeed, agents, saving, onSave, onCancel }) => {
	const isEdit = !!listing;
	const [form, setForm] = useState<FormState>(() => initialState(listing));

	// MAX-4194: the dirty baseline for the free-text agent box, and the one-shot seed resync.
	// See the SupplementalForm counterpart for the full reasoning — the two must behave
	// identically, since they share buildAgentFields.
	const [agentNameBaseline, setAgentNameBaseline] = useState<string>(
		() => listing?.agentName || agentNameSeed || ""
	);
	const agentBoxTouched = React.useRef(false);
	const seedApplied = React.useRef(false);
	React.useEffect(() => {
		if (seedApplied.current || agentBoxTouched.current || flags.agentControl !== "freeText") {
			return;
		}
		if (!agentNameSeed) {
			return; // seed not resolved yet — stay armed, this is the deep-link race
		}
		seedApplied.current = true;
		if (form.agentName !== "") {
			return; // seed resolved but the box is already populated: nothing to do, now disarmed
		}
		setAgentNameBaseline(agentNameSeed);
		setForm((prev) => ({ ...prev, agentName: agentNameSeed }));
	}, [agentNameSeed, form.agentName, flags.agentControl]);
	const [submitted, setSubmitted] = useState(false);
	// Bumped to remount the RichTextEditor on Reset — Quill won't re-sync from a changed value prop once
	// its internal state is populated.
	const [descriptionKey, setDescriptionKey] = useState(0);

	// Status / Property Type / Address radios lay out in a row on tablet+ and stack in a column on mobile.
	const theme = Mui.useTheme();
	const radiosRow = Mui.useMediaQuery(theme.breakpoints.up("sm"));

	const defaultDescription = (listing && listing.remarks) || "";
	// True while the editor still shows the live MLS remarks (seeded/untouched, or after Reset) — compared
	// on text content since Quill re-wraps the seed in HTML on mount.
	const usingMlsDefault =
		!!defaultDescription && htmlToText(form.propDescription) === htmlToText(defaultDescription);

	const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	// Reset the description back to the live MLS default. Persisting this clears the override (see
	// resolveDescription), so the listing resumes showing the feed's remarks.
	const resetDescription = () => {
		set("propDescription", defaultDescription);
		setDescriptionKey((k) => k + 1);
	};

	// Only persist propDescription when it differs from the live MLS remarks. Matching the default (the
	// seeded/untouched state, or after Reset) stores null so the public page keeps showing the live feed.
	const resolveDescription = (): string | null => {
		if (usingMlsDefault) {
			return null;
		}
		return form.propDescription || null;
	};

	const isSold = OffMarketStatus.getById(form.status)?.sold ?? false;
	const showAgentDropdown = flags.agentControl === "dropdown";
	const showAgentFreeText = flags.agentControl === "freeText";
	const agentOptions = useMemo(() => [NO_AGENT, ...agents], [agents]);

	const streetNameError = useMemo(() => {
		if (form.fullPartialAddress !== "N" && form.streetName.trim().length < 3) {
			return "Street Name should have at least 3 characters.";
		}
		return "";
	}, [form.fullPartialAddress, form.streetName]);

	const soldPriceError = useMemo(() => {
		const value = toNumberOrNull(form.soldPrice);
		if (value != null && value > 2000000000) {
			return "Sold price may not exceed $2,000,000,000";
		}
		return "";
	}, [form.soldPrice]);

	const hasErrors = !!streetNameError || !!soldPriceError;

	const handleSave = async () => {
		setSubmitted(true);
		if (hasErrors) {
			return;
		}
		// MAX-4194: built once so the same object decides both what is sent and what the new
		// baseline becomes.
		const agentFields = buildAgentFields(form, flags.agentControl, listing ? agentNameBaseline : null);
		const body: UpsertOffMarketListingResource = {
			boardId: listing ? listing.boardId : 0,
			listingNumber: listing ? listing.listingNumber : null,
			status: form.status,
			showOnWebsite: form.showOnWebsite,
			// MAX-4194: agent keys come from shared/agent-fields — do not inline. Deleting this
			// spread compiles silently (Step 10 made all three keys optional, and TypeScript does
			// not excess-property-check spread-contributed keys).
			//
			// All three land here even though the originals sat 17 keys apart — one spread cannot
			// occupy two positions, and this spot keeps the agent block beside showOnWebsite.
			...agentFields,
			propertyType: form.propertyType,
			streetNumber: form.streetNumber || null,
			streetName: form.streetName || null,
			unitNumber: form.unitNumber || null,
			city: form.city || null,
			state: form.state || null,
			zip: form.zip || null,
			fullPartialAddress: form.fullPartialAddress || null,
			bedrooms: form.bedrooms,
			fullBaths: form.fullBaths,
			halfBaths: form.halfBaths,
			squareFeet: toNumberOrNull(form.squareFeet),
			yearBuilt: form.yearBuilt || null,
			listPrice: toNumberOrNull(form.listPrice),
			soldPrice: toNumberOrNull(form.soldPrice),
			showSoldPrice: form.showSoldPrice,
			soldDate: form.soldDate ? form.soldDate : null,
			propDescription: resolveDescription(),
			mapYn: form.mapYn,
			// F1: when SOLD, CF omits the tour/extra-link fields from the update entirely
			// (soldsmodify_output_1.cfm:459,497-533) so prior tour data is preserved. Sending
			// false/null here would wipe it via the server's containsField gating, so we leave the six
			// fields off the body when SOLD rather than nulling them.
			...(isSold
				? {}
				: {
						tourYn: form.tourYn,
						tourUrl: form.tourUrl || null,
						tourLink: form.tourLink || null,
						extraLinkYn: form.extraLinkYn,
						extraLinkUrl: form.extraLinkUrl || null,
						extraLinkText: form.extraLinkText || null,
				  }),
		};
		const saved = await onSave(body);
		// MAX-4194: re-baseline to what was actually written — see the SupplementalForm
		// counterpart. Without this a second agent edit in the same page visit is compared
		// against a stale baseline and silently emits nothing.
		if (saved && listing && agentFields.agentName !== undefined) {
			setAgentNameBaseline(agentFields.agentName);
		}
	};

	const switchField = (label: string, key: keyof FormState) => (
		<Mui.FormControlLabel
			control={
				<Mui.Switch
					color="primary"
					checked={!!(form as any)[key]}
					onChange={(e) => set(key as any, e.target.checked as any)}
				/>
			}
			label={label}
		/>
	);

	// Each section renders as its own card (emulating /settings/listing-details).
	const sectionCard = (content: React.ReactNode) => (
		<Mui.Box mb={2}>
			<FullPageCard>
				<Mui.Grid container direction="column" spacing={2} style={{ maxWidth: 560 }}>
					{content}
				</Mui.Grid>
			</FullPageCard>
		</Mui.Box>
	);

	return (
		<>
			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Property Information</Mui.Typography>
					</Mui.Grid>

					<Mui.Grid item>{switchField("Show On Website", "showOnWebsite")}</Mui.Grid>

					<Mui.Grid item>
						<Mui.FormControl component="fieldset">
							<Mui.FormLabel component="legend" style={{ fontWeight: 600 }}>Status</Mui.FormLabel>
							<Mui.RadioGroup
								row={radiosRow}
								value={form.status}
								onChange={(e) => set("status", e.target.value)}
								data-testid="off-market-status"
							>
								{OffMarketStatus.values().map((status) => (
									<Mui.FormControlLabel
										key={status.id}
										value={status.id}
										control={<Mui.Radio color="primary" size="small" />}
										label={status.label}
									/>
								))}
							</Mui.RadioGroup>
						</Mui.FormControl>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.TextField
							fullWidth label="Street Number" variant="outlined" size="small"
							inputProps={{ maxLength: 20, "data-testid": "off-market-street-number" }}
							value={form.streetNumber} onChange={(e) => set("streetNumber", e.target.value)}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.TextField
							fullWidth label="Street Name" variant="outlined" size="small"
							inputProps={{ maxLength: 50, "data-testid": "off-market-street-name" }}
							value={form.streetName} onChange={(e) => set("streetName", e.target.value)}
							error={submitted && !!streetNameError}
							helperText={submitted ? streetNameError : ""}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.TextField
							fullWidth label="Unit Number" variant="outlined" size="small"
							inputProps={{ maxLength: 50 }}
							value={form.unitNumber} onChange={(e) => set("unitNumber", e.target.value)}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.TextField
							fullWidth label="City" variant="outlined" size="small"
							inputProps={{ maxLength: 50, "data-testid": "off-market-city" }}
							value={form.city} onChange={(e) => set("city", e.target.value)}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Grid container spacing={2}>
							<Mui.Grid item xs={6}>
								<Mui.TextField
									fullWidth label="State" variant="outlined" size="small"
									inputProps={{ maxLength: 2 }}
									value={form.state} onChange={(e) => set("state", e.target.value)}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={6}>
								<Mui.TextField
									fullWidth label="Zip" variant="outlined" size="small"
									inputProps={{ maxLength: 20 }}
									value={form.zip} onChange={(e) => set("zip", e.target.value)}
								/>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.FormControl component="fieldset">
							<Mui.FormLabel component="legend" style={{ fontWeight: 600 }}>Address Display</Mui.FormLabel>
							<Mui.RadioGroup row={radiosRow} value={form.fullPartialAddress} onChange={(e) => set("fullPartialAddress", e.target.value)}>
								<Mui.FormControlLabel value="F" control={<Mui.Radio color="primary" size="small" />} label="House # & Street" />
								<Mui.FormControlLabel value="P" control={<Mui.Radio color="primary" size="small" />} label="Street only" />
								<Mui.FormControlLabel value="N" control={<Mui.Radio color="primary" size="small" />} label="Neither" />
							</Mui.RadioGroup>
						</Mui.FormControl>
					</Mui.Grid>

					{!isEdit && (
						<Mui.Grid item>
							<Mui.FormControl component="fieldset">
								<Mui.FormLabel component="legend" style={{ fontWeight: 600 }}>Property Type</Mui.FormLabel>
								<Mui.RadioGroup row={radiosRow} value={form.propertyType} onChange={(e) => set("propertyType", e.target.value)}>
									{OffMarketPropertyType.values().map((pt) => (
										<Mui.FormControlLabel key={pt.id} value={pt.id} control={<Mui.Radio color="primary" size="small" />} label={pt.label} />
									))}
								</Mui.RadioGroup>
							</Mui.FormControl>
						</Mui.Grid>
					)}

					<Mui.Grid item>
						<Select
							fullWidth
							variant="outlined"
							label="Bedrooms"
							value={form.bedrooms}
							options={BEDROOM_OPTIONS}
							valueExtractor={(option: NumberOption) => option.value}
							labelExtractor={(option: NumberOption) => option.label}
							optionRenderer={(option: NumberOption) => <Mui.ListItemText primary={option.label} />}
							onChange={(value) => set("bedrooms", value == null || value === "" ? 0 : Number(value))}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Grid container spacing={2}>
							<Mui.Grid item xs={6}>
								<Select
									fullWidth
									variant="outlined"
									label="Full Baths"
									value={form.fullBaths}
									options={FULL_BATH_OPTIONS}
									valueExtractor={(option: NumberOption) => option.value}
									labelExtractor={(option: NumberOption) => option.label}
									optionRenderer={(option: NumberOption) => <Mui.ListItemText primary={option.label} />}
									onChange={(value) => set("fullBaths", value == null || value === "" ? 0 : Number(value))}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={6}>
								<Select
									fullWidth
									variant="outlined"
									label="Half Baths"
									value={form.halfBaths}
									options={HALF_BATH_OPTIONS}
									valueExtractor={(option: NumberOption) => option.value}
									labelExtractor={(option: NumberOption) => option.label}
									optionRenderer={(option: NumberOption) => <Mui.ListItemText primary={option.label} />}
									onChange={(value) => set("halfBaths", value == null || value === "" ? 0 : Number(value))}
								/>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.TextField fullWidth label="Square Feet" variant="outlined" size="small"
							inputProps={{ maxLength: 10 }}
							value={form.squareFeet} onChange={(e) => set("squareFeet", e.target.value)} />
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.TextField fullWidth label="Year Built" variant="outlined" size="small"
							inputProps={{ maxLength: 10 }}
							value={form.yearBuilt} onChange={(e) => set("yearBuilt", e.target.value)} />
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.TextField fullWidth label="List Price" variant="outlined" size="small"
							InputProps={{ startAdornment: <Mui.InputAdornment position="start">$</Mui.InputAdornment> }}
							inputProps={{ maxLength: 10 }}
							value={form.listPrice} onChange={(e) => set("listPrice", e.target.value)} />
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.TextField fullWidth label="Sold Price" variant="outlined" size="small"
							InputProps={{ startAdornment: <Mui.InputAdornment position="start">$</Mui.InputAdornment> }}
							inputProps={{ maxLength: 10, "data-testid": "off-market-sold-price" }}
							value={form.soldPrice} onChange={(e) => set("soldPrice", e.target.value)}
							error={submitted && !!soldPriceError}
							helperText={submitted ? soldPriceError : ""} />
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.TextField fullWidth label="Sold Date" type="date" variant="outlined" size="small"
							InputLabelProps={{ shrink: true }}
							value={form.soldDate} onChange={(e) => set("soldDate", e.target.value)} />
					</Mui.Grid>
					<Mui.Grid item>{switchField("Show Sold Price", "showSoldPrice")}</Mui.Grid>

					{showAgentDropdown && (
						<>
							<Mui.Grid item>
								<Select
									fullWidth
									variant="outlined"
									label="Agent"
									value={form.agentId}
									options={agentOptions}
									valueExtractor={(option: AgentOption) => option.id}
									labelExtractor={(option: AgentOption) => option.name}
									optionRenderer={(option: AgentOption) => <Mui.ListItemText primary={option.name} />}
									onChange={(value) => set("agentId", value == null || value === "" ? 0 : Number(value))}
								/>
							</Mui.Grid>
							<Mui.Grid item>{switchField("Show On Agent Page", "showOnAgentPage")}</Mui.Grid>
						</>
					)}
					{showAgentFreeText && (
						<Mui.Grid item>
							<Mui.TextField fullWidth label="Agent Name" variant="outlined" size="small"
								value={form.agentName}
								onChange={(e) => {
									// MAX-4194: disarm the seed permanently — see the effect above.
									agentBoxTouched.current = true;
									set("agentName", e.target.value);
								}} />
						</Mui.Grid>
					)}
				</>
			)}

			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Map Settings</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>{switchField("Map ON?", "mapYn")}</Mui.Grid>
					<Mui.Grid item>
						<Mui.Typography variant="body2" color="textSecondary" component="div">
							If set to Yes:
							<ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
								<li>
									the property detail page for this listing will include a "Map Location" link
									that shows the property on a map. The link will only appear if the full address
									can be displayed.
								</li>
								<li>
									listings pages that include a map (e.g., search results) will include this
									listing on the map.
								</li>
							</ul>
							If you do not want to include the Map Location link or do not want the listing to
							appear on the results map, set this option to No. It may be useful to set this option
							to No if the listing is being displayed on the map incorrectly.
						</Mui.Typography>
					</Mui.Grid>
				</>
			)}

			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Description</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						{/* Reset lives in the editor toolbar; shown only when a custom description is in effect
						    (not while the editor still displays the live MLS remarks). */}
						<RichTextEditor
							key={descriptionKey}
							toolbarId="off-market-desc-toolbar"
							value={form.propDescription}
							onChange={(value: string) => set("propDescription", value)}
							onReset={defaultDescription && !usingMlsDefault ? resetDescription : undefined}
							resetTitle="Reset to MLS default"
						/>
					</Mui.Grid>
					{defaultDescription && (
						<Mui.Grid item>
							<Mui.Box display="flex" alignItems="flex-start">
								<FeatherIcon fontSize="small">
									<Icons.Info />
								</FeatherIcon>
								<Mui.Typography variant="caption" color="textSecondary" style={{ marginLeft: 6 }}>
									A saved description replaces the MLS remarks on your listing's public page.
								</Mui.Typography>
							</Mui.Box>
						</Mui.Grid>
					)}
				</>
			)}

			{!isSold &&
				sectionCard(
					<>
						<Mui.Grid item>
							<Mui.Typography variant="h6">Other Settings</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item data-testid="off-market-tour-toggle">
							{switchField("Show Virtual Tour", "tourYn")}
						</Mui.Grid>
						{form.tourYn && (
							<>
								<Mui.Grid item>
									<Mui.TextField fullWidth label="Tour URL" variant="outlined" size="small"
										inputProps={{ maxLength: 1024, "data-testid": "off-market-tour-url" }}
										value={form.tourUrl} onChange={(e) => set("tourUrl", e.target.value)} />
								</Mui.Grid>
								<Mui.Grid item>
									<Mui.TextField fullWidth label="Tour Link Text" variant="outlined" size="small"
										inputProps={{ maxLength: 64 }}
										value={form.tourLink} onChange={(e) => set("tourLink", e.target.value)} />
								</Mui.Grid>
							</>
						)}
						<Mui.Grid item data-testid="off-market-extralink-toggle">
							{switchField("Show Extra Link", "extraLinkYn")}
						</Mui.Grid>
						{form.extraLinkYn && (
							<>
								<Mui.Grid item>
									<Mui.TextField fullWidth label="Extra Link URL" variant="outlined" size="small"
										inputProps={{ maxLength: 128, "data-testid": "off-market-extra-url" }}
										value={form.extraLinkUrl} onChange={(e) => set("extraLinkUrl", e.target.value)} />
								</Mui.Grid>
								<Mui.Grid item>
									<Mui.TextField fullWidth label="Extra Link Text" variant="outlined" size="small"
										inputProps={{ maxLength: 64 }}
										value={form.extraLinkText} onChange={(e) => set("extraLinkText", e.target.value)} />
								</Mui.Grid>
							</>
						)}
					</>
				)}

			<Mui.Box mb={2}>
				<Mui.Grid container spacing={2}>
					<Mui.Grid item>
						<Mui.Button
							variant="contained" color="primary"
							onClick={handleSave}
							disabled={saving}
							data-testid="off-market-save"
						>
							Save Property Information
						</Mui.Button>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button variant="outlined" onClick={onCancel} disabled={saving}>
							Cancel
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Box>
		</>
	);
};
